/**
 * Audit trail persistence — batch-flush in-memory review entries to the
 * backend `audit_trail` table.  Best-effort: failures are silently swallowed.
 */
import { apiFetch, API_BASE } from './apiFetch'
import { getAccessToken } from './auth'
import type { AuditEntry } from '@/components/evidence/AuditTrail'

// ── Action mapping ──────────────────────────────────────────────────────────

const ACTION_MAP: [string, string][] = [
  ['Source removed', 'review.source_removed'],
  ['Source restored', 'review.source_restored'],
  ['Badge updated', 'review.badge_updated'],
  ['Suggested fix removed', 'review.fix_cleared'],
  ['Suggested fix restored', 'review.fix_restored'],
  ['Initial analysis', 'review.analysis_seeded'],
]

function deriveAction(description: string): string {
  for (const [prefix, action] of ACTION_MAP) {
    if (description.startsWith(prefix)) return action
  }
  return 'review.action'
}

// ── Payload builder ─────────────────────────────────────────────────────────

interface TrailEntry {
  action: string
  /** D13: stable across re-audits. Looked up via claimSnapshots[claimId].claimUuid. */
  claim_uuid: string | null
  /** Optional, kept for backward compat with rows that pre-date claim_uuid keying. */
  claim_index: string
  description: string
  entry_type: string
  occurred_at: string
}

function buildPayload(
  auditLog: Map<string, AuditEntry[]>,
  persistedCounts: Map<string, number>,
  claimUuidByPanelClaimId: Map<string, string>,
): TrailEntry[] {
  const entries: TrailEntry[] = []

  for (const [claimIndex, claimEntries] of auditLog) {
    const alreadySent = persistedCounts.get(claimIndex) ?? 0
    const claimUuid = claimUuidByPanelClaimId.get(claimIndex) ?? null
    for (let i = alreadySent; i < claimEntries.length; i++) {
      const e = claimEntries[i]
      entries.push({
        action: deriveAction(e.action),
        claim_uuid: claimUuid,
        claim_index: claimIndex,
        description: e.action,
        entry_type: e.type,
        occurred_at: e.timestamp,
      })
    }
  }
  return entries
}

// ── Flush functions ─────────────────────────────────────────────────────────

/** Async flush via apiFetch. Returns updated persisted counts on success. */
export async function flushAuditTrail(
  auditId: string,
  auditLog: Map<string, AuditEntry[]>,
  persistedCounts: Map<string, number>,
  claimUuidByPanelClaimId: Map<string, string>,
): Promise<Map<string, number>> {
  const entries = buildPayload(auditLog, persistedCounts, claimUuidByPanelClaimId)
  if (entries.length === 0) return persistedCounts

  await apiFetch(`/api/audits/${auditId}/trail`, {
    method: 'POST',
    body: JSON.stringify({ entries }),
  })

  const next = new Map(persistedCounts)
  for (const [claimIndex, claimEntries] of auditLog) {
    next.set(claimIndex, claimEntries.length)
  }
  return next
}

/** Fire-and-forget flush for beforeunload — uses fetch with keepalive. */
export function beaconFlushAuditTrail(
  auditId: string,
  auditLog: Map<string, AuditEntry[]>,
  persistedCounts: Map<string, number>,
  claimUuidByPanelClaimId: Map<string, string>,
): void {
  const entries = buildPayload(auditLog, persistedCounts, claimUuidByPanelClaimId)
  if (entries.length === 0) return

  const token = getAccessToken()
  fetch(`${API_BASE}/api/audits/${auditId}/trail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ entries }),
    keepalive: true,
  }).catch(() => {}) // best-effort
}
